import React from 'react';
import { Box, Typography, Spec, Row, Col, Image } from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../../api/services/lupp/order/dto.order';
import lucidCarImage from '../../../../../../assets/lucid_car.png';
import { useStore } from '../../../../../../store/store';
import { formatCurrency, parseLocalePrice } from '../../../../../../utils/tools';
import Price from '../../../../../../components/Checkout/VehicleCard/Price/Price';
import styles from './Configuration.module.scss';

type ConfigurationProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels?: Record<string, string>;
  dataTestId?: string;
  isExpanded?: boolean;
};

/**
 * Configuration Details card component
 * Displays vehicle configuration information with image
 *
 * @component
 * @param {ConfigurationProps} props - Component props
 * @returns {JSX.Element} Configuration details card
 * @example
 * <Configuration orderData={orderData} theme="light" />
 */
const Configuration: React.FC<ConfigurationProps> = ({
  orderData,
  theme,
  labels,
  dataTestId = 'configuration-details',
  isExpanded = false,
}) => {
  const locale = useStore((state) => state.locale);
  const configurationDetails = orderData?.configurationDetails;

  const vehicleImageUri = configurationDetails?.imageUrl || lucidCarImage;

  return (
    <Box pb={8} data-testid={dataTestId}>
      {!isExpanded && (
        <Row gutter={[24, 32]} align="middle" justify="center" id="configuration-overview-row">
          <Col sm={12} xl={8}>
            <Image
              uri_mobile={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_tablet={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_desktop={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              alt={orderData.model || 'Vehicle'}
              className={styles.overviewImage}
              theme={theme}
              rounded={true}
            />
          </Col>
          <Col sm={12} xl={4}>
            <Row gutter={[16, 0]}>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['modelYear'] ?? 'Model Year'}
                  title={configurationDetails?.year || '-'}
                />
              </Col>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['modelTrim'] ?? 'Model/Trim'}
                  title={configurationDetails?.trim || configurationDetails?.model || '-'}
                />
              </Col>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['vin'] ?? 'VIN'}
                  title={orderData?.vin || configurationDetails?.currentVin || '-'}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isExpanded && (
        <Row gutter={[24, 32]} align="middle" justify="center" id="configuration-pricing-row">
          <Col sm={12} xl={12}>
            <Image
              uri_mobile={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_tablet={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_desktop={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              alt={configurationDetails?.model || 'Vehicle'}
              className={styles.pricingImage}
              theme={theme}
              rounded={true}
            />
          </Col>
          <Col sm={12} xl={12}>
            <Box pr={8} pl={8}>
              <Row gutter={[16, 8]}>
                {configurationDetails?.configurationItems?.map((item) => (
                  <Col sm={12} xl={12} key={`config-item-${item.name}`}>
                    <Row gutter={[16, 0]}>
                      <Col sm={8} xl={8}>
                        <Typography variant="body" size="extraSmall" theme={theme} color="black">
                          {item.name}
                        </Typography>
                      </Col>
                      <Col sm={4} xl={4}>
                        <Typography
                          variant="body"
                          size="extraSmall"
                          theme={theme}
                          color="black"
                          className={styles.rightAlign}
                        >
                          {typeof item.label === 'string'
                            ? item.label
                            : formatCurrency(item.label, locale)}
                        </Typography>
                      </Col>
                    </Row>
                  </Col>
                ))}
              </Row>

              <hr className={styles.divider} />
              <Price
                label={
                  labels?.['vehicle.subtotalExclVat'] ?? 'Subtotal (excl. VAT, and other costs)'
                }
                value={parseLocalePrice(configurationDetails?.totalConfigPriceExclTax)}
                theme={theme}
                isBold
              />
              <hr className={styles.divider} />

              <Box mt={8}>
                <Price
                  label={configurationDetails?.modelVariantLabel || '-'}
                  value={parseLocalePrice(configurationDetails?.modelVariantValue)}
                  theme={theme}
                  isBold
                />
              </Box>

              <Row gutter={[16, 8]} className={styles.rowBottomGap}>
                <Col sm={12} xl={12}>
                  <Row gutter={[16, 0]}>
                    <Col sm={8} xl={8}>
                      <Typography variant="body" size="extraSmall" theme={theme} color="black">
                        {labels?.['vehicle.options'] ?? 'Options'}
                      </Typography>
                    </Col>
                    <Col sm={4} xl={4}>
                      <Typography
                        variant="body"
                        size="extraSmall"
                        theme={theme}
                        color="black"
                        className={styles.rightAlign}
                      >
                        {formatCurrency(configurationDetails?.options || null, locale)}
                      </Typography>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {configurationDetails?.feesNonTaxable && (
                <Row gutter={[16, 8]} className={styles.rowBottomGap}>
                  {Object.entries(configurationDetails.feesNonTaxable).map(([key, value]) => (
                    <Col sm={12} xl={12} key={`tax-${key}`}>
                      <Row gutter={[16, 0]}>
                        <Col sm={8} xl={8}>
                          <Typography variant="body" size="extraSmall" theme={theme} color="black">
                            {key}
                          </Typography>
                        </Col>
                        <Col sm={4} xl={4}>
                          <Typography
                            variant="body"
                            size="extraSmall"
                            theme={theme}
                            color="black"
                            className={styles.rightAlign}
                          >
                            {formatCurrency(value, locale)}
                          </Typography>
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              )}
              <Row gutter={[16, 8]} className={styles.rowBottomGap}>
                <Col sm={12} xl={12}>
                  <Row gutter={[16, 0]}>
                    <Col sm={8} xl={8}>
                      <Typography variant="body" size="extraSmall" theme={theme} color="black">
                        {labels?.['vehicle.priceAdjustment'] ?? 'Price Adjustment'}
                      </Typography>
                    </Col>
                    <Col sm={4} xl={4}>
                      <Typography
                        variant="body"
                        size="extraSmall"
                        theme={theme}
                        color="black"
                        className={styles.rightAlign}
                      >
                        {formatCurrency(configurationDetails?.priceAdjustment || null, locale)}
                      </Typography>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row gutter={[16, 8]} className={styles.rowBottomGap}>
                <Col sm={12} xl={12}>
                  <Row gutter={[16, 0]}>
                    <Col sm={8} xl={8}>
                      <Typography variant="body" size="extraSmall" theme={theme} color="black">
                        {`Tax (${configurationDetails?.taxPercentage || 0}%)`}
                      </Typography>
                    </Col>
                    <Col sm={4} xl={4}>
                      <Typography
                        variant="body"
                        size="extraSmall"
                        theme={theme}
                        color="black"
                        className={styles.rightAlign}
                      >
                        {formatCurrency(configurationDetails?.taxValue || null, locale)}
                      </Typography>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {configurationDetails?.total && (
                <Box mt={8}>
                  <Price
                    label={labels?.['vehicle.subtotal'] ?? 'Subtotal'}
                    value={parseLocalePrice(configurationDetails?.total)}
                    theme={theme}
                    isBold
                  />
                </Box>
              )}
              {configurationDetails?.feesTaxable && (
                <Row gutter={[16, 8]}>
                  {Object.entries(configurationDetails.feesTaxable).map(([key, value]) => (
                    <Col sm={12} xl={12} key={`discount-${key}`}>
                      <Row gutter={[16, 0]}>
                        <Col sm={8} xl={8}>
                          <Typography variant="body" size="extraSmall" theme={theme} color="black">
                            {key}
                          </Typography>
                        </Col>
                        <Col sm={4} xl={4}>
                          <Typography
                            variant="body"
                            size="extraSmall"
                            theme={theme}
                            color="black"
                            className={styles.rightAlign}
                          >
                            {formatCurrency(value, locale)}
                          </Typography>
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              )}

              <hr className={styles.divider} />
              <Price
                label={labels?.['vehicle.total'] ?? 'Total*'}
                value={parseLocalePrice(configurationDetails?.total)}
                theme={theme}
                isBold
              />
              <Box pt={16}>
                <Typography variant="body" size="extraSmall" theme={theme} color="gray-5">
                  {labels?.['vehicle.totalNote'] ??
                    '* Total price including VAT, destination, roadworthy, delivery, documentation and registration costs.'}
                </Typography>
              </Box>
            </Box>
          </Col>
        </Row>
      )}
    </Box>
  );
};

Configuration.displayName = 'Configuration';

export default Configuration;
